import { Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { BlymLoading } from '../../../components/loading';
import { MobileDrawerWithAutoClose } from '../../../components/mobile-drawer';
import { MobileHeader } from '../../../components/mobile-header';
import { type Organization } from '../../../entity/api/organization';
import {
  OrganizationInviteStatus,
  OrganizationUserRole,
} from '../../../entity/api/organization-user';
import { addTrackCrumb } from '../../../error/tracking';
import { getQueryClient } from '../../../sdk/request';
import {
  getAuthUserFromCache,
  getStores,
  useOrganizationUsersByUserId,
  useOrganizationsList,
} from '../../../services/storage';
import { UIKey } from '../../../services/storage/ui-key-value';
import { SidebarSettings } from './-settings-components/-sidebar';

export const Route = createFileRoute('/app/settings/_sidebar-settings-layout')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/settings/_sidebar-settings-layout',
      level: 'info',
    });
  },

  // eslint-disable-next-line no-use-before-define
  component: SidebarLayout,
});

export function SidebarLayout() {
  const queryClient = getQueryClient();
  // TanStack Query hooks
  const currentUser = getAuthUserFromCache(queryClient);
  const { data: currentOrgUsersData } = useOrganizationUsersByUserId(currentUser?._id);
  const { data: organizationsData } = useOrganizationsList();
  const isOrgLoading = getStores().uiKeyValue(function (state) {
    return state.getFromStore(UIKey.IS_ORG_LOADING) ?? false;
  });
  const [orgItems, setOrgItems] = useState<
    {
      organization: Organization;
      isOwner: boolean;
      isActive: boolean;
    }[]
  >([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const location = useRouterState({
    select: (state) => state.location,
  });

  useEffect(
    function () {
      const orgItemsData = organizationsData ?? [];
      const orgUsersData = currentOrgUsersData ?? [];
      const newOrgItems = [] as typeof orgItems;

      for (let i = 0; i < orgItemsData.length; i += 1) {
        const org = orgItemsData[i];
        if (org == null) {
          continue;
        }

        // make sure the organization has been accepted
        const userOrg = orgUsersData.find(
          (uo) =>
            uo != null &&
            uo.organizationId === org._id &&
            uo.inviteStatus === OrganizationInviteStatus.Accepted
        );
        if (userOrg == null) {
          continue;
        }

        const isActive = location.pathname.includes(org._id);
        const isOwner =
          orgUsersData.find(function (orgUser) {
            return orgUser?.organizationId === org._id;
          })?.role === OrganizationUserRole.Owner;

        newOrgItems.push({
          organization: org,
          isOwner,
          isActive,
        });
      }

      setOrgItems(newOrgItems);
    },
    [organizationsData, currentOrgUsersData, location.pathname]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Mobile Header - shown only on mobile */}
      <div className="mobile:block desktop:hidden">
        <MobileHeader
          onMenuClick={function () {
            setIsMobileMenuOpen(true);
          }}
        />
      </div>

      {/* Mobile Drawer - shown only on mobile */}
      <div className="mobile:block desktop:hidden">
        <MobileDrawerWithAutoClose
          open={isMobileMenuOpen}
          onOpenChange={setIsMobileMenuOpen}
          pathname={location.pathname}
        >
          <SidebarSettings organizationData={orgItems} variant="mobile" />
        </MobileDrawerWithAutoClose>
      </div>

      <div className="flex h-full w-full">
        {/* Desktop Sidebar - shown only on desktop */}
        <div className="mobile:hidden desktop:flex">
          <SidebarSettings organizationData={orgItems} variant="desktop" />
        </div>

        {/* Main Content Area */}
        <div className="flex h-full w-full flex-col overflow-hidden mobile:pt-16">
          {isOrgLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <BlymLoading />
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
}
